import { findPeriodByMob } from './cohortSheetPeriodValue';
import { getCohortRoas100Counts } from './cohortSheetSummary';
import { fmtMoney } from './formatters';
import { hasCohortSheet } from './roasSaturation';

export const ROAS_OVERVIEW_REVENUE_DELAY_MONTHS = 3;
export const ROAS_OVERVIEW_FIRST_CASH_MONTH = 3;
const ROAS_100_THRESHOLD = 1;

function periodCumulativeRoas(period, marketingSpend) {
  if (period?.roas != null && Number.isFinite(Number(period.roas))) {
    return Number(period.roas);
  }
  const spend = Number(marketingSpend) || 0;
  if (spend <= 0 || period?.revenue == null) {
    return null;
  }
  return (Number(period.revenue) || 0) / spend;
}

function cohortMarketingSpend(cohort) {
  const spend = Number(cohort?.marketingSpend) || 0;
  return spend > 0 ? spend : null;
}

function getCohortPeriodRoas(cohort, mob) {
  const spend = cohortMarketingSpend(cohort);
  if (spend == null) {
    return null;
  }
  const period = findPeriodByMob(cohort.periods, mob);
  return periodCumulativeRoas(period, spend);
}

function getSortedCohortPeriods(cohort) {
  return [...(cohort.periods ?? [])].sort(
    (left, right) => Number(left.mob) - Number(right.mob)
  );
}

function getCohortBreakevenMob(cohort, { actualOnly = false } = {}) {
  const spend = cohortMarketingSpend(cohort);
  if (spend == null) {
    return null;
  }
  const periods = getSortedCohortPeriods(cohort);
  for (const period of periods) {
    if (actualOnly && period.isPredicted) {
      continue;
    }
    const roas = periodCumulativeRoas(period, spend);
    if (roas != null && roas >= ROAS_100_THRESHOLD) {
      return Number(period.mob);
    }
  }
  return null;
}

function getCohortLastPeriodRoas(cohort) {
  const spend = cohortMarketingSpend(cohort);
  if (spend == null) {
    return { mob: null, roas: null };
  }
  const periods = getSortedCohortPeriods(cohort);
  const lastPeriod = periods[periods.length - 1];
  if (!lastPeriod) {
    return { mob: null, roas: null };
  }
  return {
    mob: Number(lastPeriod.mob),
    roas: periodCumulativeRoas(lastPeriod, spend),
  };
}

function buildPendingCohortForecast(cohort, index) {
  const cohortLabel = cohort.cohortDate || `Cohort ${index + 1}`;
  const breakevenMob = getCohortBreakevenMob(cohort, { actualOnly: false });
  const monthFromSpend = monthsFromSpendWithDelay(breakevenMob);
  if (monthFromSpend != null) {
    return {
      cohortLabel,
      reaches100: true,
      monthFromSpend,
    };
  }
  const lastPeriod = getCohortLastPeriodRoas(cohort);
  return {
    cohortLabel,
    reaches100: false,
    lastRoasPct: lastPeriod.roas != null ? lastPeriod.roas * 100 : null,
    lastMonthFromSpend: monthsFromSpendWithDelay(lastPeriod.mob),
  };
}

export function monthsFromSpendWithDelay(mob, delayMonths = ROAS_OVERVIEW_REVENUE_DELAY_MONTHS) {
  if (mob == null || !Number.isFinite(Number(mob))) {
    return null;
  }
  return Number(mob) + 1 + delayMonths;
}

export function mobForMonthFromSpendWithDelay(
  monthFromSpend,
  delayMonths = ROAS_OVERVIEW_REVENUE_DELAY_MONTHS
) {
  if (monthFromSpend == null || !Number.isFinite(Number(monthFromSpend))) {
    return null;
  }
  return Number(monthFromSpend) - 1 - delayMonths;
}

function median(values) {
  const sorted = values.filter((value) => value != null && Number.isFinite(value)).sort(
    (left, right) => left - right
  );
  if (!sorted.length) {
    return null;
  }
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function average(values) {
  const valid = values.filter((value) => value != null && Number.isFinite(value));
  if (!valid.length) {
    return null;
  }
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function formatMonthLabel(monthValue) {
  if (monthValue == null || !Number.isFinite(Number(monthValue))) {
    return '—';
  }
  const rounded = Math.round(Number(monthValue) * 10) / 10;
  return Number.isInteger(rounded) ? `Month ${rounded}` : `Month ${rounded.toFixed(1)}`;
}

function formatPct(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  return `${Math.round(Number(value))}%`;
}

function formatCountShare(count, total) {
  if (!total) {
    return '—';
  }
  const pct = Math.round((count / total) * 100);
  return `${count} of ${total} (${pct}%)`;
}

function formatCohortDateRange(dates) {
  const valid = dates.filter(Boolean);
  if (!valid.length) {
    return '';
  }
  if (valid.length === 1) {
    return valid[0];
  }
  return `${valid[0]} to ${valid[valid.length - 1]}`;
}

export function buildRoasOverviewData({
  cohortSheet,
  companyName = 'Company',
}) {
  if (!hasCohortSheet(cohortSheet)) {
    return null;
  }

  const cohorts = (cohortSheet.cohorts ?? []).filter((cohort) => cohortMarketingSpend(cohort) != null);
  const roas100Counts = getCohortRoas100Counts(cohortSheet);
  const totalMarketingInvested = cohorts.reduce(
    (sum, cohort) => sum + (Number(cohort.marketingSpend) || 0),
    0
  );

  const breakevenMonths = cohorts
    .map((cohort) => {
      const mob = getCohortBreakevenMob(cohort, { actualOnly: false });
      return monthsFromSpendWithDelay(mob);
    })
    .filter((value) => value != null);

  const firstCashMob = ROAS_OVERVIEW_FIRST_CASH_MONTH - 1;
  const firstCashRoasValues = cohorts
    .map((cohort) => getCohortPeriodRoas(cohort, firstCashMob))
    .filter((value) => value != null)
    .map((value) => value * 100);
  const firstCashRoasPct = average(firstCashRoasValues);

  const firstCohort = cohorts[0];
  const lastCohort = cohorts[cohorts.length - 1];
  const spendRampLabel =
    firstCohort && lastCohort
      ? `${fmtMoney(firstCohort.marketingSpend)} → ${fmtMoney(lastCohort.marketingSpend)}`
      : '—';
  const spendRampDates = formatCohortDateRange([
    firstCohort?.cohortDate,
    lastCohort?.cohortDate,
  ]);

  const pendingCohortForecasts = cohorts
    .map((cohort, index) => ({ cohort, index }))
    .filter(({ cohort }) => getCohortBreakevenMob(cohort, { actualOnly: true }) == null)
    .map(({ cohort, index }) => buildPendingCohortForecast(cohort, index));
  const pendingCount = pendingCohortForecasts.length;

  return {
    companyName: companyName?.trim() || 'Company',
    totalCohorts: roas100Counts.totalCohorts,
    actualAtOrAbove100: roas100Counts.actualAtOrAbove100,
    totalMarketingInvested,
    medianBreakevenMonth: median(breakevenMonths),
    averageBreakevenMonth: average(breakevenMonths),
    firstCashRoasPct,
    spendRampLabel,
    spendRampDates,
    pendingCount,
    pendingCohortForecasts,
    formatMonthLabel,
    formatPct,
    formatCountShare,
  };
}

export function buildRoasOverviewParagraphs(data) {
  if (!data) {
    return [];
  }

  const paragraphs = [
    'Initial review of Return on Ad Spend (ROAS): measured on cohort revenue against marketing spend',
    `No. of cohorts underwritten: ${data.totalCohorts}`,
    `No. of cohorts that reached 100% ROAS to date: ${data.actualAtOrAbove100}`,
  ];

  const avgBreakeven = data.averageBreakevenMonth;
  const medianBreakeven = data.medianBreakevenMonth;
  if (avgBreakeven != null || medianBreakeven != null) {
    const parts = [];
    if (avgBreakeven != null) {
      parts.push(`avg ${avgBreakeven.toFixed(1)} months`);
    }
    if (medianBreakeven != null) {
      parts.push(`${formatMonthLabel(medianBreakeven).toLowerCase()}`);
    }
    paragraphs.push(`Cohort months to breakeven: ${parts.join('; ')}`);
  }

  paragraphs.push(
    `Total investment in marketing to date: ${fmtMoney(data.totalMarketingInvested)}`
  );

  if (data.pendingCount > 0) {
    paragraphs.push('Forecast for cohorts not yet at 100% ROAS (based on model):');
    paragraphs.push(
      `Cohorts not yet at 100% ROAS to date: ${data.pendingCount} of ${data.totalCohorts}.`
    );
    for (const forecast of data.pendingCohortForecasts) {
      if (forecast.reaches100) {
        paragraphs.push(
          `${forecast.cohortLabel}: modeled to reach 100% ROAS at ${formatMonthLabel(forecast.monthFromSpend).toLowerCase()}`
        );
        continue;
      }
      const lastRoas = data.formatPct(forecast.lastRoasPct);
      if (forecast.lastMonthFromSpend != null && lastRoas !== '—') {
        paragraphs.push(
          `${forecast.cohortLabel}: not modeled to reach 100% ROAS (${lastRoas} at ${formatMonthLabel(forecast.lastMonthFromSpend).toLowerCase()})`
        );
        continue;
      }
      paragraphs.push(`${forecast.cohortLabel}: not modeled to reach 100% ROAS`);
    }
  }

  return paragraphs;
}
